// src/pages/chat/settings/profile/upload-image/upload-image.hooks.ts
import { useEffect, useMemo, useRef, useState } from "react";

type UseUploadImageArgs = {
    file: File | null;
    maxSize?: number; // output px (square)
    previewSize?: number; // preview circle px (square)
};

type DragState = {
    dragging: boolean;
    startX: number;
    startY: number;
    startOffsetX: number; // stored in OUTPUT space
    startOffsetY: number; // stored in OUTPUT space
};

function clamp(n: number, min: number, max: number) {
    return Math.max(min, Math.min(max, n));
}

function isImageFile(file: File) {
    return /^image\//i.test(file.type || "");
}

async function readImageAsHTMLImageElement(
    file: File,
): Promise<HTMLImageElement> {
    const blobUrl = URL.createObjectURL(file);

    try {
        const img = new Image();
        img.decoding = "async";
        img.loading = "eager";

        await new Promise<void>((resolve, reject) => {
            img.onload = () => resolve();
            img.onerror = () => reject(new Error("Failed to load image"));
            img.src = blobUrl;
        });

        return img;
    } finally {
        // NOTE: we revoke later (after any draw ops) via cleanup in hook
    }
}

function canvasToJpegFile(
    canvas: HTMLCanvasElement,
    fileNameBase: string,
    quality = 0.9,
): Promise<File> {
    return new Promise((resolve, reject) => {
        canvas.toBlob(
            (blob) => {
                if (!blob) return reject(new Error("Failed to encode jpeg"));
                resolve(
                    new File([blob], `${fileNameBase}.jpg`, {
                        type: "image/jpeg",
                    }),
                );
            },
            "image/jpeg",
            quality,
        );
    });
}

export const useUploadImage = (args: UseUploadImageArgs) => {
    const { file, maxSize = 600, previewSize = 240 } = args;

    const [error, setError] = useState<string | null>(null);
    const [img, setImg] = useState<HTMLImageElement | null>(null);
    const [objectUrl, setObjectUrl] = useState<string | null>(null);

    // Zoom: 1..3 feels like LinkedIn-ish
    const [zoom, setZoom] = useState(1.2);

    // Pan offsets stored in OUTPUT space (maxSize pixels)
    const [offsetX, setOffsetX] = useState(0);
    const [offsetY, setOffsetY] = useState(0);

    const dragRef = useRef<DragState>({
        dragging: false,
        startX: 0,
        startY: 0,
        startOffsetX: 0,
        startOffsetY: 0,
    });

    const hasFile = !!file;

    useEffect(() => {
        let active = true;
        setError(null);
        setImg(null);

        // Reset controls when file changes
        setZoom(1.2);
        setOffsetX(0);
        setOffsetY(0);

        if (!file) return;

        if (!isImageFile(file)) {
            setError("Please choose an image file.");
            return;
        }

        const url = URL.createObjectURL(file);
        setObjectUrl(url);

        (async () => {
            try {
                const image = await readImageAsHTMLImageElement(file);
                if (!active) return;
                setImg(image);
            } catch (e: any) {
                if (!active) return;
                setError(e?.message || "Failed to load image.");
            }
        })();

        return () => {
            active = false;
            try {
                if (url) URL.revokeObjectURL(url);
            } catch {
                // ignore
            }
        };
    }, [file]);

    const previewUrl = useMemo(() => objectUrl, [objectUrl]);

    // ✅ Convert OUTPUT-space offsets to PREVIEW-space offsets for live preview transform
    const previewScale = previewSize / maxSize;
    const previewOffsetX = offsetX * previewScale;
    const previewOffsetY = offsetY * previewScale;

    // --- Rendering / export math ---
    // We render into a square "output" space of maxSize x maxSize.
    // We scale the image so it covers the square, then apply zoom & pan.
    const computeCoverScale = (image: HTMLImageElement) => {
        const iw = image.naturalWidth || image.width;
        const ih = image.naturalHeight || image.height;
        if (!iw || !ih) return 1;

        // cover: max of ratios
        const base = Math.max(maxSize / iw, maxSize / ih);
        return base;
    };

    const clampOffsetsToKeepCovering = (
        image: HTMLImageElement,
        nextZoom: number,
        nextX: number,
        nextY: number,
    ) => {
        const iw = image.naturalWidth || image.width;
        const ih = image.naturalHeight || image.height;
        if (!iw || !ih) return { x: nextX, y: nextY };

        const base = computeCoverScale(image);
        const scale = base * nextZoom;

        const drawW = iw * scale;
        const drawH = ih * scale;

        // Centered draw origin is (maxSize - drawW)/2 + offsetX, same for Y.
        // To cover, we need originX <= 0 and originX + drawW >= maxSize.
        // => offsetX between [ (maxSize - drawW)/2 .. (drawW - maxSize)/2 ]
        const minX = (maxSize - drawW) / 2; // most left
        const maxX = (drawW - maxSize) / 2; // most right (positive)

        const minY = (maxSize - drawH) / 2;
        const maxY = (drawH - maxSize) / 2;

        return {
            x: clamp(nextX, minX, maxX),
            y: clamp(nextY, minY, maxY),
        };
    };

    const onZoomChange = (next: number) => {
        setError(null);

        const nextZoom = clamp(next, 1, 3);

        if (img) {
            const clamped = clampOffsetsToKeepCovering(
                img,
                nextZoom,
                offsetX,
                offsetY,
            );
            setOffsetX(clamped.x);
            setOffsetY(clamped.y);
        }

        setZoom(nextZoom);
    };

    const onPointerDown = (clientX: number, clientY: number) => {
        dragRef.current = {
            dragging: true,
            startX: clientX,
            startY: clientY,
            startOffsetX: offsetX,
            startOffsetY: offsetY,
        };
    };

    const onPointerMove = (clientX: number, clientY: number) => {
        if (!dragRef.current.dragging) return;
        if (!img) return;

        const dx = clientX - dragRef.current.startX;
        const dy = clientY - dragRef.current.startY;

        // ✅ CRITICAL FIX:
        // Map preview-space drag pixels -> output-space pixels
        // so export matches what user composed in the preview.
        const dragToOutput = maxSize / previewSize;

        const nextX = dragRef.current.startOffsetX + dx * dragToOutput;
        const nextY = dragRef.current.startOffsetY + dy * dragToOutput;

        const clamped = clampOffsetsToKeepCovering(img, zoom, nextX, nextY);
        setOffsetX(clamped.x);
        setOffsetY(clamped.y);
    };

    const onPointerUp = () => {
        dragRef.current.dragging = false;
    };

    const exportJpeg = async () => {
        setError(null);

        if (!file) throw new Error("No file selected");
        if (!img) throw new Error("Image not loaded");

        // Draw to output canvas (maxSize x maxSize)
        const canvas = document.createElement("canvas");
        canvas.width = maxSize;
        canvas.height = maxSize;

        const ctx = canvas.getContext("2d");
        if (!ctx) throw new Error("Canvas not supported");

        // Fill background (important for JPG transparency)
        ctx.fillStyle = "#000000";
        ctx.fillRect(0, 0, maxSize, maxSize);

        const iw = img.naturalWidth || img.width;
        const ih = img.naturalHeight || img.height;

        const base = computeCoverScale(img);
        const scale = base * zoom;

        const drawW = iw * scale;
        const drawH = ih * scale;

        // ✅ offsetX/offsetY are OUTPUT-space and now correctly correspond to preview composition
        const originX = (maxSize - drawW) / 2 + offsetX;
        const originY = (maxSize - drawH) / 2 + offsetY;

        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = "high";
        ctx.drawImage(img, originX, originY, drawW, drawH);

        // Encode jpeg
        const baseName = (file.name || "avatar")?.replace(/\.[^.]+$/, "") ||
            "avatar";
        return await canvasToJpegFile(canvas, baseName, 0.9);
    };

    return {
        hasFile,
        error,
        previewUrl,
        imgLoaded: !!img,
        zoom,

        // ✅ expose PREVIEW-space offsets for UI transform
        previewOffsetX,
        previewOffsetY,

        setError,
        onZoomChange,

        onPointerDown,
        onPointerMove,
        onPointerUp,

        exportJpeg,
    };
};
